import * as _ from "lodash-es";
import type {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableOptions
} from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import type { PPAContract } from "../PPAContractPage.types";
import { getConsumptionSitesColumn } from "./columns/getConsumptionSitesColumn";
import { getGenerationSitesColumn } from "./columns/getGenerationSitesColumn";
import { getNameColumn } from "./columns/getNameColumn";
import { getEndColumn } from "./columns/getNextEndDateColumn";
import { getNextTerminationDeadlineColumn } from "./columns/getNextTerminationDeadlineColumn";
import { getServiceRecipientsColumn } from "./columns/getServiceRecipientsColumn";
import { getBeginColumn } from "./columns/getStartDateColumn";
import { getStatusColumn } from "./columns/getStatusColumn";
import { getSupplierColumn } from "./columns/getSupplierColumn";

interface PPAContractTableProps {
  contracts: Array<PPAContract>;
}

function PPAContractTable({ contracts }: PPAContractTableProps) {
  const defaultOptions = getDefaultMRTOptions<PPAContract>({
    emptyRowsFallbackText: "Keine Verträge vorhanden.",
    initialState: {
      showGlobalFilter: true
    },
    searchTextInput: {
      placeholder: "Suche nach Vertrag"
    }
  });

  const tableOptions: Omit<
    MRT_TableOptions<PPAContract>,
    "columns" | "data"
  > = _.merge({}, defaultOptions, {
    enableTopToolbar: true,

    filterFns: {
      filterRelevantColumns: (
        row: MRT_Row<PPAContract>,
        _,
        filterValue: string
      ) => {
        const lowerCaseFilterValue = filterValue.toLowerCase();
        const name = row.getValue<string>("name");
        const contractCircleName = row.getValue<string>("contract_circle.name");
        const supplier = row.getValue<string>("supplier.id");
        const serviceRecipients =
          row.getValue<PPAContract["service_recipients"]>("service_recipients");
        const consumptionSites =
          row.getValue<Array<string>>("consumption_sites");
        const generationSites = row.getValue<Array<string>>("generation_sites");

        return (
          name.toLowerCase().includes(lowerCaseFilterValue) ||
          contractCircleName.toLowerCase().includes(lowerCaseFilterValue) ||
          supplier?.toLowerCase().includes(lowerCaseFilterValue) ||
          serviceRecipients.some((serviceRecipient) =>
            serviceRecipient.name.toLowerCase().includes(lowerCaseFilterValue)
          ) ||
          consumptionSites.some((site) =>
            site.toLowerCase().includes(lowerCaseFilterValue)
          ) ||
          generationSites.some((site) =>
            site.toLowerCase().includes(lowerCaseFilterValue)
          )
        );
      }
    },
    globalFilterFn: "filterRelevantColumns",

    columnFilterDisplayMode: "popover",
    enableColumnResizing: true
  });

  const columnsMRT: Array<MRT_ColumnDef<PPAContract>> = useMemo(() => {
    return [
      getNameColumn(),
      {
        accessorKey: "contract_circle.name",
        header: "Vertragskreis",
        enableColumnFilter: false
      },
      getStatusColumn(),
      getBeginColumn(),
      getEndColumn(),
      getNextTerminationDeadlineColumn(),
      getSupplierColumn(contracts),
      getGenerationSitesColumn(contracts),
      getServiceRecipientsColumn(contracts),
      getConsumptionSitesColumn(contracts)
    ];
  }, [contracts]);

  const table = useMantineReactTable({
    ...tableOptions,
    columns: columnsMRT,
    data: contracts
  });

  return <MantineReactTable table={table} />;
}

export { PPAContractTable, PPAContractTableProps };
